import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  Check,
  Eye,
  ListChecks,
  Loader2,
  Lock,
  Pause,
  Pencil,
  Play,
  Plus,
  Rocket,
  Trash2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { ConfirmDestructiveDialog } from "@/components/ui/ConfirmDestructiveDialog";
import { ScheduleJobDialog } from "@/components/schedule/ScheduleJobDialog";
import { ScheduleRunsDrawer } from "@/components/schedule/ScheduleRunsDrawer";
import {
  useScheduleJobs,
  type ScheduleJobView,
  type ScheduleRunRow,
} from "@/hooks/useScheduleJobs";

/**
 * `ScheduleView` — the AGENDAMENTOS pane (agendamentos plan P1; R2 handoff
 * §3.1). The CLOCK beside Pendências' LIST: the jobs the assistant runs on a
 * schedule.
 *
 * Discipline that is load-bearing here:
 *  - **Always render `scheduleHuman`** — the runtime owns that wording so both
 *    surfaces (pane + prompt) describe a job identically. Never re-derive a
 *    cadence from `schedule` (the "todo dia útil" flag is not readable off a
 *    cron expression).
 *  - **Locked system rows are READ-ONLY with a badge** and carry NO mutation
 *    control (operator ruling D-9: users SEE them for transparency, never
 *    change them). Where the row belongs to the tasklist machinery it also
 *    carries the "alimenta Pendências" hint — the designed intersection made
 *    legible instead of hidden.
 *  - **`SCHEDULE_DISABLED` fills the pane** with the runtime's own PT-BR
 *    message; an empty list would be a lie about the assistant's schedule.
 *
 * P1 is READ-ONLY: create/edit/pause/delete land in P2, the run-history
 * drawer + nav-op in P3.
 */

const KIND_ICON: Record<string, LucideIcon> = {
  reminder: Bell,
  task: ListChecks,
  watch: Eye,
  mission: Rocket,
  system: Lock,
};

const KIND_TONE: Record<string, string> = {
  reminder: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
  task: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  watch: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
  mission: "bg-gold-400/20 text-gold-700 dark:bg-gold-500/20 dark:text-gold-400",
  system: "bg-cream-200 text-coffee-600 dark:bg-coffee-800 dark:text-cream-400",
};

/** System jobs whose output IS the Pendências pulse — the designed
 *  intersection worth naming on the row (plan §1). */
function feedsTasklist(job: ScheduleJobView): boolean {
  return job.locked && /task-inspector|tasklist/i.test(job.id);
}

function LastResult({ run }: { run: ScheduleRunRow | undefined }) {
  const { t } = useTranslation();
  if (!run) {
    return (
      <span className="text-[11px] text-coffee-500 dark:text-cream-500">
        {t("schedule.noRecentRuns")}
      </span>
    );
  }
  const when = new Date(run.ts).toLocaleString();
  const tone =
    run.status === "ok"
      ? "text-emerald-700 dark:text-emerald-300"
      : run.status === "error"
        ? "text-red-700 dark:text-red-300"
        : "text-coffee-500 dark:text-cream-500";
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11px]", tone)}>
      {run.status === "ok" ? (
        <Check size={11} aria-hidden />
      ) : run.status === "error" ? (
        <AlertTriangle size={11} aria-hidden />
      ) : null}
      {t(`schedule.runStatus.${run.status}`, run.status)} · {when}
    </span>
  );
}

export function ScheduleView() {
  const { t } = useTranslation();
  const { gate, jobs, lastRun, refresh } = useScheduleJobs();
  const rpc = useGatewayStore((s) => s.rpc);

  /** Which dialog is open: create, edit <job>, or none. */
  const [editing, setEditing] = useState<{ job?: ScheduleJobView } | null>(null);
  /** The job pending a typed-phrase delete confirm (D-9). */
  const [deleting, setDeleting] = useState<ScheduleJobView | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  /** The job whose run history is open (P3 drawer). */
  const [historyFor, setHistoryFor] = useState<ScheduleJobView | null>(null);

  async function togglePause(job: ScheduleJobView) {
    if (!rpc || busyId) return;
    setBusyId(job.id);
    setRowError(null);
    try {
      await rpc.call(job.enabled ? "schedule.pause" : "schedule.resume", { jobId: job.id });
      refresh();
    } catch (err) {
      // Runtime messages are user-facing PT-BR — verbatim.
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  /** Fires ONLY after the typed-phrase gate passes (D-9: every destructive op
   *  on this surface types the phrase). Soft-delete — history is preserved. */
  async function confirmDelete() {
    if (!rpc || !deleting) return;
    setBusyId(deleting.id);
    setDeleteError(null);
    try {
      await rpc.call("schedule.delete", { jobId: deleting.id });
      setDeleting(null);
      refresh();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  /** Create/edit + the typed-phrase delete gate — rendered by every branch
   *  that can reach them (the list AND the empty state's create button). */
  const dialogs = (
    <>
      {editing && (
        <ScheduleJobDialog
          job={editing.job}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {historyFor && (
        <ScheduleRunsDrawer job={historyFor} onClose={() => setHistoryFor(null)} />
      )}
      {deleting && (
        <ConfirmDestructiveDialog
          open
          onClose={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
          title={t("schedule.deleteDialog.title", { title: deleting.title })}
          description={t("schedule.deleteDialog.description")}
          bullets={
            t("schedule.deleteDialog.bullets", {
              returnObjects: true,
            }) as unknown as readonly string[]
          }
          confirmPhrase={t("schedule.deleteDialog.confirmPhrase")}
          confirmPromptLabel={t("schedule.deleteDialog.confirmPromptLabel")}
          confirmButtonLabel={t("schedule.deleteDialog.confirmButtonLabel")}
          cancelButtonLabel={t("common.cancel", "Cancelar")}
          onConfirm={() => void confirmDelete()}
          busy={busyId === deleting.id}
          error={deleteError}
          severity="destructive"
        />
      )}
    </>
  );

  const createButton = (
    <button
      type="button"
      data-testid="schedule-create"
      onClick={() => setEditing({})}
      className="inline-flex items-center gap-1.5 rounded-md bg-gold-500 px-3 py-1.5 text-body-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400"
    >
      <Plus size={14} aria-hidden />
      {t("schedule.createButton")}
    </button>
  );

  if (gate.state === "loading") {
    return (
      <div
        className="flex flex-1 items-center justify-center gap-2 p-6 text-body-sm text-coffee-500 dark:text-cream-500"
        data-testid="schedule-loading"
      >
        <Loader2 size={14} className="animate-spin" aria-hidden />
        {t("common.loading", "Carregando…")}
      </div>
    );
  }

  // The kill-switch (and an older runtime) fill the pane with the honest
  // reason — never an empty list that would read as "nothing scheduled".
  if (gate.state === "disabled" || gate.state === "unavailable") {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center"
        data-testid={
          gate.state === "disabled" ? "schedule-disabled" : "schedule-unavailable"
        }
      >
        <CalendarClock size={28} className="text-cream-500 dark:text-coffee-600" aria-hidden />
        <h3 className="font-serif-display text-lg text-coffee-900 dark:text-cream-100">
          {t(
            gate.state === "disabled" ? "schedule.disabledTitle" : "schedule.unavailableTitle",
          )}
        </h3>
        {/* The runtime's message is already user-facing PT-BR (§2.3) — render
            it verbatim rather than inventing our own wording. */}
        <p className="max-w-md text-body-sm text-coffee-600 dark:text-cream-400">
          {gate.state === "disabled" ? gate.message : t("schedule.unavailableBody")}
        </p>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center"
        data-testid="schedule-empty"
      >
        <CalendarClock size={28} className="text-cream-500 dark:text-coffee-600" aria-hidden />
        <h3 className="font-serif-display text-lg text-coffee-900 dark:text-cream-100">
          {t("schedule.emptyTitle")}
        </h3>
        <p className="max-w-md text-body-sm text-coffee-600 dark:text-cream-400">
          {t("schedule.emptyBody")}
        </p>
        <div className="mt-3">{createButton}</div>
        {dialogs}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3" data-testid="schedule-view">
      <div className="mb-2 flex justify-end">{createButton}</div>
      <ul className="space-y-2">
        {jobs.map((job) => {
          const Icon = KIND_ICON[job.kind] ?? CalendarClock;
          return (
            <li
              key={job.id}
              data-testid="schedule-job-row"
              data-job-id={job.id}
              data-locked={job.locked ? "true" : "false"}
              className={cn(
                "rounded-lg border px-3 py-2.5",
                job.locked
                  ? "border-cream-200/70 bg-cream-50/60 dark:border-coffee-800 dark:bg-coffee-950/40"
                  : "border-cream-200/80 bg-white/70 dark:border-coffee-700/70 dark:bg-coffee-900/50",
                !job.enabled && "opacity-70",
              )}
            >
              <div className="flex items-start gap-2.5">
                <span
                  aria-hidden
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                    KIND_TONE[job.kind] ?? KIND_TONE.system,
                  )}
                >
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="min-w-0 truncate text-body-sm font-medium text-coffee-900 dark:text-cream-100">
                      {job.title}
                    </p>
                    {job.locked && (
                      <span
                        data-testid="schedule-locked-badge"
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-cream-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-coffee-600 dark:bg-coffee-800 dark:text-cream-400"
                      >
                        <Lock size={9} aria-hidden />
                        {t("schedule.lockedBadge")}
                      </span>
                    )}
                    {!job.enabled && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-cream-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:bg-coffee-800 dark:text-cream-500">
                        <Pause size={9} aria-hidden />
                        {t("schedule.pausedBadge")}
                      </span>
                    )}
                    {job.enabled && !job.locked && (
                      <Play
                        size={10}
                        aria-hidden
                        className="shrink-0 text-emerald-600 dark:text-emerald-400"
                      />
                    )}
                  </div>
                  {/* ALWAYS the runtime's own wording. */}
                  <p
                    data-testid="schedule-human"
                    className="truncate text-[11px] text-coffee-600 dark:text-cream-400"
                  >
                    {job.scheduleHuman}
                  </p>
                  {feedsTasklist(job) && (
                    <p
                      data-testid="schedule-feeds-tasklist"
                      className="mt-0.5 text-[11px] italic text-coffee-500 dark:text-cream-500"
                    >
                      {t("schedule.feedsTasklist")}
                    </p>
                  )}
                  {/* History is a READ — offered on every row, including
                      locked system ones (transparency, D-9). */}
                  <button
                    type="button"
                    data-testid={`schedule-history-${job.id}`}
                    onClick={() => setHistoryFor(job)}
                    title={t("schedule.runs.open")}
                    className="mt-1 rounded text-left transition-colors hover:opacity-80"
                  >
                    <LastResult run={lastRun.get(job.id)} />
                  </button>
                </div>
                {/* Mutation controls exist ONLY for user jobs — a locked
                    system row carries none at all (D-9), so `LOCKED` is
                    unreachable from this surface by construction. */}
                {!job.locked && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      data-testid={`schedule-toggle-${job.id}`}
                      onClick={() => void togglePause(job)}
                      disabled={busyId === job.id}
                      aria-label={t(job.enabled ? "schedule.pause" : "schedule.resume")}
                      title={t(job.enabled ? "schedule.pause" : "schedule.resume")}
                      className="rounded-md p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 disabled:opacity-50 dark:text-cream-500 dark:hover:bg-coffee-800"
                    >
                      {busyId === job.id ? (
                        <Loader2 size={13} aria-hidden className="animate-spin" />
                      ) : job.enabled ? (
                        <Pause size={13} aria-hidden />
                      ) : (
                        <Play size={13} aria-hidden />
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid={`schedule-edit-${job.id}`}
                      onClick={() => setEditing({ job })}
                      aria-label={t("schedule.edit")}
                      title={t("schedule.edit")}
                      className="rounded-md p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800"
                    >
                      <Pencil size={13} aria-hidden />
                    </button>
                    <button
                      type="button"
                      data-testid={`schedule-delete-${job.id}`}
                      onClick={() => {
                        setDeleteError(null);
                        setDeleting(job);
                      }}
                      aria-label={t("schedule.delete")}
                      title={t("schedule.delete")}
                      className="rounded-md p-1.5 text-red-600 transition-colors hover:bg-red-500/10 dark:text-red-400"
                    >
                      <Trash2 size={13} aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {rowError && (
        <p
          className="mt-2 rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300"
          data-testid="schedule-row-error"
        >
          {rowError}
        </p>
      )}
      <p className="mt-3 px-1 text-[11px] text-coffee-500 dark:text-cream-500">
        {t("schedule.paneNote")}
      </p>
      {dialogs}
    </div>
  );
}
